import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, User, LogOut, Trash2, Pencil } from 'lucide-react';
import { profileAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Leaderboards from './Leaderboards';

export default function ProfileSelector({ onSelectProfile }) {
  const { logout, user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newProfileName, setNewProfileName] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingProfileId, setDeletingProfileId] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);
  const [editProfileName, setEditProfileName] = useState('');

  // Rabbit avatar options
  const rabbitAvatars = [
    { id: 'grey_happy', image: '/avatars/rabbit_grey_happy.png', name: 'Happy Grey' },
    { id: 'white_bow', image: '/avatars/rabbit_white_bow.png', name: 'Bow White' },
    { id: 'brown_glasses', image: '/avatars/rabbit_brown_glasses.png', name: 'Studious' },
    { id: 'spotted_carrot', image: '/avatars/rabbit_spotted_carrot.png', name: 'Muncher' },
    { id: 'grey_cool', image: '/avatars/rabbit_grey_cool.png', name: 'Cool' },
    { id: 'white_angel', image: '/avatars/rabbit_white_angel.png', name: 'Angel' },
    { id: 'tan_sporty', image: '/avatars/rabbit_tan_sporty.png', name: 'Sporty' },
    { id: 'grey_sleepy', image: '/avatars/rabbit_grey_sleepy.png', name: 'Sleepy' },
    { id: 'black_star', image: '/avatars/rabbit_black_star.png', name: 'Star' },
    { id: 'cream_flower', image: '/avatars/rabbit_cream_flower.png', name: 'Flower' }
  ];
  const [selectedAvatar, setSelectedAvatar] = useState(rabbitAvatars[0].image);

  useEffect(() => {
    loadProfiles();
  }, []);

  const handleDeleteProfile = async (profileId, profileName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the profile "${profileName}"?\n\nThis will permanently delete all progress, points, and streak data associated with this profile.\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      setDeletingProfileId(profileId);
      await profileAPI.deleteProfile(profileId);
      
      // Reload profiles after successful deletion
      await loadProfiles();
      
      // Show success message (optional - could use a toast here)
      alert(`Profile "${profileName}" has been deleted successfully.`);
    } catch (error) {
      console.error('Failed to delete profile:', error);
      alert('Failed to delete profile. Please try again.');
    } finally {
      setDeletingProfileId(null);
    }
  };

  const handleEditProfile = async () => {
    if (!editProfileName.trim() || !editingProfile) return;
    
    try {
      const updatedProfile = await profileAPI.update(editingProfile.id, { name: editProfileName.trim() });
      
      // Update local state
      setProfiles(profiles.map(p => 
        p.id === editingProfile.id ? { ...p, name: updatedProfile.name } : p
      ));
      
      // Close dialog
      setEditingProfile(null);
      setEditProfileName('');
      
      alert(`Profile name updated to "${updatedProfile.name}" successfully.`);
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to update profile name. Please try again.');
    }
  };


  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await profileAPI.getAll();
      setProfiles(data);
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async () => {
    if (newProfileName.trim() && !creating) {
      try {
        setCreating(true);
        const newProfile = await profileAPI.create(newProfileName, selectedAvatar);
        setProfiles([...profiles, newProfile]);
        setNewProfileName('');
        setIsDialogOpen(false);
      } catch (error) {
        console.error('Error creating profile:', error);
        alert('Failed to create profile. Please try again.');
      } finally {
        setCreating(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="Bible Thumper Logo" 
            className="w-32 h-32 mx-auto mb-4 rounded-2xl shadow-lg"
          />
          <h1 className="text-5xl font-bold text-blue-900 mb-3">Bible Thumper: Teen Edition</h1>
          <p className="text-xl text-blue-700">Choose your profile to continue your journey</p>
        </div>

        {/* Responsive layout: Leaderboards on top (mobile), Profiles on left (desktop) */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Profiles Section - Order 2 on mobile, Order 1 on desktop */}
          <div className="flex-1 order-2 lg:order-1">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-xl text-blue-700">Loading profiles...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
            {profiles.map((profile) => (
              <Card
                key={profile.id}
                className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-blue-200 bg-white"
                onClick={async () => {
                  try {
                    // Set profile_id in session before selecting profile
                    await profileAPI.setProfile(profile.id);
                    onSelectProfile(profile);
                  } catch (error) {
                    console.error('Failed to set profile in session:', error);
                    // Still allow profile selection even if session update fails
                    onSelectProfile(profile);
                  }
                }}
              >
                <CardHeader className="text-center pb-4">
                  <img 
                    src={profile.avatar} 
                    alt={profile.name}
                    className="w-24 h-24 mx-auto mb-3 rounded-full object-cover"
                    onError={(e) => {
                      e.target.src = '/logo.png';
                    }}
                  />
                  <CardTitle className="text-2xl text-blue-900">{profile.name}</CardTitle>
                </CardHeader>
                <CardContent className="text-center space-y-2">
                  <div className="flex justify-around text-sm">
                    <div className="bg-blue-100 px-4 py-2 rounded-full">
                      <p className="font-semibold text-blue-900">🔥 {profile.streak}</p>
                      <p className="text-xs text-blue-700">Day Streak</p>
                    </div>
                    <div className="bg-green-100 px-4 py-2 rounded-full">
                      <p className="font-semibold text-green-900">⭐ {profile.totalPoints}</p>
                      <p className="text-xs text-green-700">Points</p>
                    </div>
                  </div>
                  
                  {/* Edit and Delete Profile Buttons */}
                  <div className="flex gap-2 mt-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingProfile(profile);
                        setEditProfileName(profile.name);
                      }}
                      variant="ghost"
                      size="sm"
                      className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit Name
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProfile(profile.id, profile.name);
                      }}
                      variant="ghost"
                      size="sm"
                      className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={deletingProfileId === profile.id}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {deletingProfileId === profile.id ? 'Deleting...' : 'Delete'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              ))}
              </div>
            )}

            <div className="flex justify-center mt-6">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-lg">
                <Plus className="mr-2" /> Add New Profile
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle className="text-2xl text-blue-900">Create New Profile</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 py-4">
                <div>
                  <label className="text-sm font-medium text-blue-900 mb-2 block">Name</label>
                  <Input
                    placeholder="Enter name"
                    value={newProfileName}
                    onChange={(e) => setNewProfileName(e.target.value)}
                    className="border-blue-200"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-blue-900 mb-2 block">Choose Your Rabbit Avatar</label>
                  <div className="grid grid-cols-5 gap-2">
                    {rabbitAvatars.map((rabbit) => (
                      <button
                        key={rabbit.id}
                        onClick={() => setSelectedAvatar(rabbit.image)}
                        className={`p-2 rounded-lg border-2 transition-all hover:scale-105 ${
                          selectedAvatar === rabbit.image
                            ? 'border-blue-500 bg-blue-50 shadow-lg'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <img
                          src={rabbit.image}
                          alt={rabbit.name}
                          className="w-full h-auto rounded"
                          onError={(e) => {
                            e.target.src = '/logo.png';
                          }}
                        />
                        <p className="text-xs text-center mt-1">{rabbit.name}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={handleCreateProfile}
                  disabled={creating}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                >
                  {creating ? 'Creating...' : 'Create Profile'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
            </div>

          {/* Edit Profile Dialog */}
          <Dialog open={!!editingProfile} onOpenChange={(open) => {
            if (!open) {
              setEditingProfile(null);
              setEditProfileName('');
            }
          }}>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle className="text-2xl text-blue-900">Edit Profile Name</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Profile Name
                  </label>
                  <Input
                    placeholder="Enter new name"
                    value={editProfileName}
                    onChange={(e) => setEditProfileName(e.target.value)}
                    className="text-lg"
                    maxLength={50}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleEditProfile();
                      }
                    }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleEditProfile}
                    disabled={!editProfileName.trim()}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Save Changes
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingProfile(null);
                      setEditProfileName('');
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          </div>

          {/* Leaderboards Section - Order 1 on mobile, Order 2 on desktop */}
          <div className="lg:w-96 flex-shrink-0 order-1 lg:order-2">
            <Leaderboards />
            {/* Mobile separator to indicate profiles are below */}
            <div className="lg:hidden mt-6 text-center">
              <p className="text-sm text-gray-600 mb-2">↓ Scroll down to select your profile ↓</p>
              <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <div className="text-center mt-8">
          <Button
            onClick={logout}
            variant="outline"
            className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout {user?.email ? `(${user.email})` : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}
